import { Component, OnInit, Input, SimpleChanges, OnChanges } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import {
  RealEstateBuildingDetails,
  RealEstateFundingProject,
} from '../../models/real-estate-project.model';
import { GraphUser } from '../../models/graph-user.model';
import { STATES, DEPARTMENTS, PROJECT_CLASS } from '../common/real-estate-form-options';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { RealEstateService } from '../../services/real-estate.service';
import * as _ from 'lodash';
import { AdaptiveIdDialogComponent } from './adaptive-id-dialog/adaptive-id-dialog.component';
import { DataService } from '../../services/data.service';
import { ConfigService } from '../../services/config.service';
import { RREFormStatus } from '../../real-estate-form-dashboard/common/real-estate-form-dashboard.types';

@Component({
  selector: 'app-real-estate-project-detail-info',
  templateUrl: './real-estate-project-detail-info.component.html',
  styleUrls: ['./real-estate-project-detail-info.component.scss'],
  standalone: false,
})
export class RealEstateProjectDetailInfoComponent implements OnInit, OnChanges {
  @Input() reviewMode = false;
  @Input() realEstateDetailForm: FormGroup;
  @Input() fm: boolean;
  @Input() pId: string;
  @Input() formStatus: RREFormStatus;

  RREFormStatus = RREFormStatus;

  buildingSearchControl = new FormControl();
  startDatePicker!: MatDatepicker<any>;
  serviceDatePicker!: MatDatepicker<any>;
  completionDatePicker!: MatDatepicker<any>;
  states = STATES;
  departments = DEPARTMENTS;
  projectClass = PROJECT_CLASS;
  fundingProjects: RealEstateFundingProject[] = [];
  buildingDetails: RealEstateBuildingDetails[] = [];
  filteredBuildingDetails: RealEstateBuildingDetails[] = [];
  buildingSearch = '';
  projectManagers = [''];
  sponsors = [''];
  contractAdmins: [''];
  assetContacts = [''];
  additionalContacts = [''];
  projectManager = {
    name: '',
    email: '',
  };
  sponsor = {
    name: '',
    email: '',
  };
  contractAdmin = {
    name: '',
    email: '',
  };
  assetContact = {
    name: '',
    email: '',
  };
  additionalContact = {
    name: '',
    email: '',
  };

  // Separate user details for each control
  projectManagerUserDetails: GraphUser[] = [];
  filteredProjectManagerUserDetails: GraphUser[] = [];

  sponsorUserDetails: GraphUser[] = [];
  filteredSponsorUserDetails: GraphUser[] = [];

  contractAdminUserDetails: GraphUser[] = [];
  filteredContractAdminUserDetails: GraphUser[] = [];

  assetUserDetails: GraphUser[] = [];
  filteredAssetUserDetails: GraphUser[] = [];

  additionalContactUserDetails: GraphUser[] = [];
  filteredAdditionalContactUserDetails: GraphUser[] = [];

  minStartDate = new Date();
  todaysDate = new Date();

  constructor(
    private dialog: MatDialog,
    private realEstateService: RealEstateService,
    private dataService: DataService,
    private config: ConfigService
  ) {}

  ngOnInit() {
    this.setupBuildingSubscription();
    this.setupServiceDateSubscription();
    this.setupContactSearchSubscription();
    this.getAssetContactOptions();
    this.minStartDate.setDate(this.minStartDate.getDate() - 5);
    this.prefillData();
    this.handleProjectIdConstraints();
  }

  prefillData() {
    if (this.realEstateDetailForm.get('building').value) {
      this.createProjectId(true);
    }
  }

  // Handle state and department constraints when projectId exists
  private handleProjectIdConstraints(): void {
    if (this.pId && !this.reviewMode) {
      // If we have a pId but not in review mode, disable state and department
      // to preserve projectID integrity while allowing other field edits
      const stateControl = this.realEstateDetailForm.get('state');
      const departmentControl = this.realEstateDetailForm.get('department');
      if (stateControl) stateControl.disable();
      if (departmentControl) departmentControl.disable();
    }
  }

  // Handle click on the adaptiveId field
  onAdaptiveIdClick(): void {
    const adaptiveIdControl = this.realEstateDetailForm.get('adaptiveId');
    if (!adaptiveIdControl.disabled) {
      this.openAdaptiveIdDialog();
    }
  }

  // Open a dialog box for the adaptiveId field
  private openAdaptiveIdDialog(): void {
    const adaptiveIdValue = this.realEstateDetailForm.get('adaptiveId')
      ? this.realEstateDetailForm.get('adaptiveId').value
      : null;

    const dialogRef = this.dialog.open(AdaptiveIdDialogComponent, {
      width: '400px',
      data: { adaptiveId: adaptiveIdValue },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const adaptiveIdControl = this.realEstateDetailForm.get('adaptiveId');
        if (adaptiveIdControl) {
          adaptiveIdControl.setValue(result);
        }
        // create the project id
        this.createProjectId();
      }
    });
  }

  createProjectId(prefill = false): void {
    const adaptiveId = this.realEstateDetailForm.get('adaptiveId').value;
    if (adaptiveId) {
      const adaptiveIdValid = this.realEstateDetailForm.get('adaptiveId').valid;
      if (!adaptiveIdValid) {
        console.warn('Adaptive ID is not valid.');
        return;
      }
    }
    const state = this.realEstateDetailForm.get('state').value;
    const department = this.realEstateDetailForm.get('department').value;

    if (state && department) {
      if (adaptiveId && !this.realEstateDetailForm.get('adaptiveId').disabled) {
        this.getProjectDetails(prefill, state, department, adaptiveId);
      } else {
        this.getProjectDetails(prefill, state, department);
      }
    } else {
      console.warn('State and Department must be filled in before making the call.');
    }
  }

  getProjectDetails(
    prefill: boolean,
    state: string,
    department: string,
    adaptiveId?: string
  ): void {
    const body = {
      state: state,
      department: department,
      adaptive_id: adaptiveId,
    };
    this.realEstateService.getProjectSetupData(body).subscribe((response) => {
      // Prefill the form with backend data
      const projectIdControl = this.realEstateDetailForm.get('projectId');
      if (projectIdControl && !this.pId && !prefill) {
        projectIdControl.setValue(response.project_id);
      }

      this.buildingDetails = response.building_list.sort((a, b) =>
        a.building_name.localeCompare(b.building_name)
      );
      // Copy all buildingDetails into filteredBuildingDetails
      this.filteredBuildingDetails = [...this.buildingDetails];

      if (prefill) this.getBuildingLocationDetails(this.realEstateDetailForm.get('building').value);

      (error) => {
        console.error('Error:', error);
      };
    });
  }
  // Subscribe to changes in the building field and call getBuildingLocationDetails
  private setupBuildingSubscription(): void {
    const buildingControl = this.realEstateDetailForm.get('building');
    if (buildingControl) {
      buildingControl.valueChanges.subscribe((building) => {
        if (building) {
          this.getBuildingLocationDetails(building);
        } else {
          console.warn('Building must be filled in before making the call.');
        }
      });
    }

    // Subscribe to changes in the search input
    this.buildingSearchControl.valueChanges.subscribe(() => {
      this.filterBuildings();
    });
  }

  // Fetch building location details based on the selected building
  private getBuildingLocationDetails(building: string) {
    const buildingDetail = this.buildingDetails.find((detail) => detail.building_name === building);
    if (buildingDetail) {
      this.realEstateDetailForm.patchValue({
        assetLocation: buildingDetail.asset_location,
        majorLocation: buildingDetail.major_location,
        city: buildingDetail.city,
        businessUnit: buildingDetail.bu,
        projectType: buildingDetail.project_type,
        omOperatingUnits: buildingDetail.om_operating_units,
        capitalOperatingUnits: buildingDetail.capital_operating_units,
        omAccountNum: buildingDetail.om_account_num,
        capitalCwipAccountNum: buildingDetail.capital_cwip_account_num,
        capitalRwipAccountNum: buildingDetail.capital_rwip_account_num,
        assetLocationId: buildingDetail.asset_location_id,
        majorLocationId: buildingDetail.major_location_id,
      });

      this.getFundingProjectDetails(buildingDetail.bu, buildingDetail.funding_project);
    } else {
      console.warn('No matching building detail found.');
    }
  }

  filterBuildings(): void {
    const searchValue = (this.buildingSearchControl.value || '').toLowerCase();
    this.filteredBuildingDetails = this.buildingDetails.filter((building) =>
      building.building_name.toLowerCase().includes(searchValue)
    );
  }

  private getFundingProjectDetails(bu: string, defaultFundingProject?: string) {
    this.realEstateService.getFundingProjectData(bu).subscribe((response) => {
      // Build the fundingProjects array, including the default from the building if not present
      const fundingList = Array.isArray(response) ? response : [];

      const fundingProjects = fundingList.map((item: any) => ({
        fundingProject: item.work_order_number,
      }));

      if (
        defaultFundingProject &&
        !fundingProjects.some((fp) => fp.fundingProject === defaultFundingProject)
      ) {
        fundingProjects.unshift({
          fundingProject: defaultFundingProject,
        });
      }
      this.fundingProjects = fundingProjects;

      (error) => {
        console.error('Error:', error);
      };
    });
  }

  // Project Description Length
  descriptionLength(): number {
    const description = this.realEstateDetailForm.get('projectDescription').value || '';
    return description.length;
  }

  // User Contact Methods
  getContactOptions(
    searchInput: string,
    controlType: 'projectManager' | 'sponsor' | 'contractAdmin' | 'additionalContact'
  ): void {
    this.realEstateService
      .userSearch(searchInput)
      .then((users) => {
        const orderedUsers = _.orderBy(users, ['surname', 'givenName']);

        switch (controlType) {
          case 'projectManager':
            this.projectManagerUserDetails = orderedUsers;
            this.filteredProjectManagerUserDetails = [...this.projectManagerUserDetails];
            this.displayGraphUser(this.projectManagerUserDetails, 'projectManager');
            break;
          case 'sponsor':
            this.sponsorUserDetails = orderedUsers;
            this.filteredSponsorUserDetails = [...this.sponsorUserDetails];
            this.displayGraphUser(this.sponsorUserDetails, 'sponsor');
            break;
          case 'contractAdmin':
            this.contractAdminUserDetails = orderedUsers;
            this.filteredContractAdminUserDetails = [...this.contractAdminUserDetails];
            this.displayGraphUser(this.contractAdminUserDetails, 'contractAdmin');
            break;
          case 'additionalContact':
            this.additionalContactUserDetails = orderedUsers;
            this.filteredAdditionalContactUserDetails = [...this.additionalContactUserDetails];
            this.displayGraphUser(this.additionalContactUserDetails, 'additionalContact');
            break;
        }
      })
      .catch((error) => {
        console.error('Error fetching users:', error);
      });
  }

  getAssetContactOptions(): void {
    const adGroup = this.config.getConfig('RealEstateAssetContactGroup');
    this.dataService.getGroupId(adGroup).subscribe((result) => {
      const groupId = (result as any).value[0].id;
      this.realEstateService.assetContactsUserSearch(groupId).subscribe(
        (result) => {
          this.assetUserDetails = _.orderBy((result as any).value, ['surname', 'givenName']);
          this.filteredAssetUserDetails = [...this.assetUserDetails];
          this.displayGraphUser(this.assetUserDetails, 'asset');
        },
        (error) => {
          console.error('Error fetching users:', error);
        }
      );
    });
  }

  // Subscribe to changes in the contact search inputs
  setupContactSearchSubscription(): void {
    this.realEstateDetailForm.get('sponsor').valueChanges.subscribe((searchInput: string) => {
      if (searchInput && searchInput.length > 2) {
        // Call the getContactOptions() method when the user types at least 3 characters
        this.getContactOptions(searchInput, 'sponsor');
      } else {
        // Clear the filtered user details if the input is empty or too short
        this.filteredSponsorUserDetails = [];
      }
    });

    this.realEstateDetailForm
      .get('projectManager')
      .valueChanges.subscribe((searchInput: string) => {
        if (searchInput && searchInput.length > 2) {
          // Call the getContactOptions() method when the user types at least 3 characters
          this.getContactOptions(searchInput, 'projectManager');
        } else {
          // Clear the filtered user details if the input is empty or too short
          this.filteredProjectManagerUserDetails = [];
        }
      });

    this.realEstateDetailForm.get('contractAdmin').valueChanges.subscribe((searchInput: string) => {
      if (searchInput && searchInput.length > 2) {
        // Call the getContactOptions() method when the user types at least 3 characters
        this.getContactOptions(searchInput, 'contractAdmin');
      } else {
        // Clear the filtered user details if the input is empty or too short
        this.filteredContractAdminUserDetails = [];
      }
    });

    this.realEstateDetailForm.get('assetContact').valueChanges.subscribe(() => {
      this.getAssetContactOptions();
    });

    this.realEstateDetailForm
      .get('additionalContact')
      .valueChanges.subscribe((searchInput: string) => {
        if (searchInput && searchInput.length > 2) {
          // Call the getContactOptions() method when the user types at least 3 characters
          this.getContactOptions(searchInput, 'additionalContact');
        } else {
          // Clear the filtered user details if the input is empty or too short
          this.filteredAdditionalContactUserDetails = [];
        }
      });
  }

  displayGraphUser(
    users: GraphUser[],
    target: 'projectManager' | 'sponsor' | 'contractAdmin' | 'asset' | 'additionalContact'
  ): any | undefined {
    const formatted = users.map((user) => ({
      ...user,
      displayName: user.displayName,
      mail: user.mail,
      formattedValue: !user
        ? ''
        : user.displayName && user.mail
          ? `${user.displayName} (${user.mail})`
          : '',
    }));

    switch (target) {
      case 'projectManager':
        this.filteredProjectManagerUserDetails = formatted;
        if (this.projectManager.name) {
          const value = formatted.find((a) => a.mail === this.projectManager.email);
          if (value) {
            this.realEstateDetailForm.get('projectManager').setValue(value);
            this.projectManager = null;
          }
        }
        break;
      case 'sponsor':
        this.filteredSponsorUserDetails = formatted;
        if (this.sponsor.name) {
          const value = formatted.find((a) => a.mail === this.sponsor.email);
          if (value) {
            this.realEstateDetailForm.get('sponsor').setValue(value);
            this.sponsor = null;
          }
        }
        break;
      case 'contractAdmin':
        this.filteredContractAdminUserDetails = formatted;
        if (this.contractAdmin.name) {
          const value = formatted.find((a) => a.mail === this.contractAdmin.email);
          if (value) {
            this.realEstateDetailForm.get('contractAdmin').setValue(value);
            this.contractAdmin = null;
          }
        }
        break;
      case 'asset':
        this.filteredAssetUserDetails = formatted;
        if (this.assetContact.name) {
          const value = formatted.find((a) => a.mail === this.assetContact.email);
          if (value) {
            this.realEstateDetailForm.get('assetContact').setValue(value);
            this.assetContact = null;
          }
        }
        break;
      case 'additionalContact':
        this.filteredAdditionalContactUserDetails = formatted;
        if (this.additionalContact.name) {
          const value = formatted.find((a) => a.mail === this.additionalContact.email);
          if (value) {
            this.realEstateDetailForm.get('additionalContact').setValue(value);
            this.additionalContact = null;
          }
        }
        break;
    }
  }

  displayUser(value: GraphUser) {
    return value ? value.formattedValue : '';
  }

  // Subscribe to change in service date and set completion date
  private setupServiceDateSubscription(): void {
    const serviceDate = this.realEstateDetailForm.get('serviceDate');

    if (serviceDate) {
      serviceDate.valueChanges.subscribe((serviceDate) => {
        if (!serviceDate) return;
        const completeDate = new Date(serviceDate);
        completeDate.setMonth(completeDate.getMonth() + 3); // Add 3 months to the service date
        const completeDateControl = this.realEstateDetailForm.get('completionDate');
        if (completeDateControl) {
          completeDateControl.setValue(completeDate); // Set the completion date in the form
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the adaptiveIdDisabled input property has changed
    if (this.realEstateDetailForm && changes['adaptiveIdDisabled']) {
      const adaptiveId = this.realEstateDetailForm.get('adaptiveId');
      if (this.realEstateDetailForm.get('adaptiveId').disabled) {
        adaptiveId.disable();
      } else {
        adaptiveId.enable();
      }
    }

    // Handle pId changes (when project is saved as draft)
    if (this.realEstateDetailForm && changes['pId']) {
      this.handleProjectIdConstraints();
    }

    // Handle reviewMode changes
    if (this.realEstateDetailForm && changes['reviewMode']) {
      if (this.reviewMode) {
        this.realEstateDetailForm.disable();
        this.buildingSearchControl.disable();
      } else {
        this.realEstateDetailForm.enable();

        // If pId exists, disable state and department to preserve projectID integrity
        if (this.pId) {
          const stateControl = this.realEstateDetailForm.get('state');
          const departmentControl = this.realEstateDetailForm.get('department');
          if (stateControl) stateControl.disable();
          if (departmentControl) departmentControl.disable();
        }

        // Ensure building field is specifically enabled when exiting review mode
        const buildingControl = this.realEstateDetailForm.get('building');
        if (buildingControl) {
          buildingControl.enable();
        }
        // Also ensure the building search control is enabled
        this.buildingSearchControl.enable();
        // Reset the search value to prevent null errors
        if (this.buildingSearchControl.value === null) {
          this.buildingSearchControl.setValue('');
        }

        // Ensure building details are loaded when exiting review mode
        // This is important for forms that were saved as draft and being edited
        if (this.buildingDetails.length === 0) {
          this.ensureBuildingDataLoaded();
        }
      }
    }
  }

  // Helper method to ensure building data is loaded for editing
  private ensureBuildingDataLoaded(): void {
    const state = this.realEstateDetailForm.get('state').value;
    const department = this.realEstateDetailForm.get('department').value;
    const adaptiveId = this.realEstateDetailForm.get('adaptiveId').value;

    if (state && department) {
      if (adaptiveId && !this.realEstateDetailForm.get('adaptiveId').disabled) {
        this.getProjectDetails(false, state, department, adaptiveId);
      } else {
        this.getProjectDetails(false, state, department);
      }
    }
  }
}
